/**
 * The letter packs — Cat & Mouse's data (specs/cat-mouse-mode.md §8.1).
 *
 * Deliberately NOT in `BUILT_IN_PACKS`: these are a treat's data, the way `sentences.<lang>.json`
 * is the Labyrinth's, not a lesson. `lessonsForLang()` picks up every built-in pack whose `lang`
 * matches, and a letter pack appearing as a lesson card is the one thing that would undo the
 * decision in §2.1 to make this a treat rather than a lesson.
 *
 * All ten languages ship. Only the seven Latin ones are pure shape A (a case pair); `ja` is shape A
 * on two scripts, `ko` a jamo predicate and `th` a singleton whose difficulty is its rivals — see
 * §9 and `game/cat/membership.ts`.
 */
import type { Pack } from './types';
import lettersDe from './letters.de.json';
import lettersCh from './letters.de-CH.json';
import lettersEn from './letters.en.json';
import lettersNl from './letters.nl.json';
import lettersEs from './letters.es.json';
import lettersFr from './letters.fr.json';
import lettersIt from './letters.it.json';
import lettersJa from './letters.ja.json';
import lettersKo from './letters.ko.json';
import lettersTh from './letters.th.json';

export const LETTER_PACKS: Pack[] = [
  lettersDe as Pack, lettersCh as Pack, lettersEn as Pack, lettersNl as Pack, lettersEs as Pack,
  lettersFr as Pack, lettersIt as Pack, lettersJa as Pack, lettersKo as Pack, lettersTh as Pack,
];

/**
 * The letter pack for a language: exact tag first (`de-CH` before `de`), then the base tag, then
 * null. Same resolution order as the pools, and the caller falls back to `en` — a treat must always
 * produce a run, even in a language whose pack has not been authored yet.
 */
export function getLettersPack(lang: string): Pack | null {
  const exact = LETTER_PACKS.find((p) => p.lang === lang);
  if (exact) return exact;
  const base = lang.toLowerCase().split('-')[0];
  return LETTER_PACKS.find((p) => p.lang.toLowerCase().split('-')[0] === base) ?? null;
}
